import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAppsT } from '@/lib/appsI18n';
import { localGet } from '@/lib/appStorage';
import type { Note, NotesState } from '@/pages/client/apps/notes/notesUtils';

interface TaskRow {
  id: string;
  title: string;
  due: string;
  priority: 'low' | 'normal' | 'high';
  status: 'todo' | 'done';
  subject: string;
}

interface PlannerRow {
  id: string;
  title: string;
  date: string;
  time: string;
  type: 'cours' | 'examen' | 'rdv' | 'revision';
}

interface TasksState {
  tasks: TaskRow[];
  events: PlannerRow[];
}

interface CardRow {
  id: string;
  question: string;
  answer: string;
  nextReview: string;
}

interface DeckRow {
  id: string;
  name: string;
  subject: string;
  cards: CardRow[];
}

interface FlashcardsState {
  decks: DeckRow[];
  xp: number;
  streak: number;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

function shiftDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * « Mon espace d'études » : la page quotidienne du compte utilisateur.
 * Elle rassemble ce qui est réellement en attente : notes à réviser,
 * échéances proches et quiz du jour. Les données viennent des mini-apps
 * (stockage local synchronisé), pas de données inventées.
 */
export default function StudySpaceTab() {
  const { user } = useAuth();
  const { t, isRtl } = useAppsT();
  const [notesToReview, setNotesToReview] = useState<Note[]>([]);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [events, setEvents] = useState<PlannerRow[]>([]);
  const [decks, setDecks] = useState<DeckRow[]>([]);
  const [xp, setXp] = useState(0);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const [notesState, tasksState, cardsState] = await Promise.all([
        localGet<NotesState>(`user::${user.id}:client_notes_v2`),
        localGet<TasksState>(`user::${user.id}:client_tasks_v2`),
        localGet<FlashcardsState>(`user::${user.id}:client_flashcards_v2`),
      ]);
      if (cancelled) return;
      setNotesToReview((notesState?.notes || []).filter((note) => note.toReview || note.important));
      setTasks((tasksState?.tasks || []).filter((task) => task.status === 'todo'));
      setEvents((tasksState?.events || []).filter((event) => event.date >= todayIso()));
      setDecks(cardsState?.decks || []);
      setXp(cardsState?.xp || 0);
      setStreak(cardsState?.streak || 0);
      setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const soon = shiftDays(7);
  const upcoming = [...tasks]
    .filter((task) => task.due <= soon)
    .sort((a, b) => a.due.localeCompare(b.due))
    .slice(0, 6);
  const agenda = [...events].sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`)).slice(0, 6);
  const dueDecks = decks.filter((deck) => deck.cards.some((card) => card.nextReview <= todayIso()));
  const quizDeck = (dueDecks[0] || decks[0]) || null;

  const deadlineLabel = (date: string) => {
    if (date === todayIso()) return "aujourd'hui";
    if (date === shiftDays(1)) return 'demain';
    return new Date(`${date}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
      </div>
    );
  }

  return (
    <div className={`max-w-5xl mx-auto space-y-5 ${isRtl ? 'text-right' : ''}`}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">{t('space.title')}</h1>
          <p className="text-sm text-foreground-500 mt-1">{t('space.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-3 py-1.5 rounded-full bg-accent-100 text-accent-900 text-xs font-bold whitespace-nowrap">
            <i className="ri-fire-fill mr-1"></i>{streak} j
          </span>
          <span className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-bold whitespace-nowrap">
            {xp} {t('cards.xp')}
          </span>
        </div>
      </div>

      {/* Objectif du jour */}
      <div className="rounded-2xl bg-foreground-950 text-background-50 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-accent-500 flex items-center justify-center flex-shrink-0">
          <i className="ri-sun-line text-xl"></i>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-background-50/60">Aujourd&apos;hui</p>
          <p className="text-sm font-semibold mt-0.5">
            {notesToReview.length + upcoming.length + (quizDeck ? 1 : 0) > 0
              ? `${notesToReview.length} note(s) à réviser · ${upcoming.length} échéance(s) · ${quizDeck ? 'un quiz prêt' : 'aucun quiz'}`
              : 'Rien d’urgent. Un bon moment pour prendre de l’avance.'}
          </p>
        </div>
        <Link
          to={quizDeck ? '/mon-compte/apps/zifek-flashcards' : '/mon-compte/apps/zifek-notes'}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-background-50 text-foreground-950 text-sm font-bold whitespace-nowrap no-underline hover:bg-background-100 flex-shrink-0"
        >
          <i className="ri-play-fill"></i>
          {t('space.studyNow')}
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* Notes à réviser */}
        <section className="bg-background-50 border border-background-200/50 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-background-200/50">
            <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-800 flex items-center justify-center flex-shrink-0">
              <i className="ri-sticky-note-line"></i>
            </span>
            <h2 className="text-xs font-bold font-heading text-foreground-950 flex-1 truncate">{t('space.toReview')}</h2>
            <span className="text-[11px] font-semibold text-foreground-400">{notesToReview.length}</span>
          </div>
          {notesToReview.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-xs text-foreground-400">{t('space.noNotes')}</p>
              <Link to="/mon-compte/apps/zifek-notes" className="mt-3 inline-block text-[11px] font-semibold text-primary-700 no-underline">
                {t('space.openNotes')} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-background-200/40 max-h-64 overflow-y-auto">
                {notesToReview.slice(0, 6).map((note) => (
                  <li key={note.id} className="flex items-start gap-2 px-4 py-2.5">
                    <i className="ri-bookmark-3-line text-accent-500 text-xs mt-1 flex-shrink-0"></i>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-foreground-900 truncate">{note.title || t('notes.untitled')}</p>
                      <p className="text-[10px] text-foreground-400 line-clamp-1">
                        {note.content.replace(/\s+/g, ' ').slice(0, 80) || t('notes.placeholder')}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <Link
                to="/mon-compte/apps/zifek-notes"
                className="block px-4 py-2.5 text-[11px] font-semibold text-primary-700 border-t border-background-200/50 no-underline hover:bg-background-100/60"
              >
                {t('space.openNotes')} <i className="ri-arrow-right-line"></i>
              </Link>
            </>
          )}
        </section>

        {/* Échéances */}
        <section className="bg-background-50 border border-background-200/50 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-background-200/50">
            <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-900 flex items-center justify-center flex-shrink-0">
              <i className="ri-calendar-check-line"></i>
            </span>
            <h2 className="text-xs font-bold font-heading text-foreground-950 flex-1 truncate">{t('space.deadlines')}</h2>
            <span className="text-[11px] font-semibold text-foreground-400">{upcoming.length}</span>
          </div>
          {upcoming.length === 0 && agenda.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-xs text-foreground-400">{t('space.noDeadlines')}</p>
              <Link to="/mon-compte/apps/zifek-tasks" className="mt-3 inline-block text-[11px] font-semibold text-primary-700 no-underline">
                {t('space.openTasks')} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-background-200/40 max-h-64 overflow-y-auto">
                {upcoming.map((task) => {
                  const late = task.due < todayIso();
                  return (
                    <li key={task.id} className="flex items-center gap-2 px-4 py-2.5">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${late ? 'bg-primary-500' : 'bg-accent-500'}`}></span>
                      <p className="text-xs font-medium text-foreground-900 truncate flex-1">{task.title}</p>
                      <span className={`text-[10px] font-semibold whitespace-nowrap ${late ? 'text-primary-700' : 'text-foreground-400'}`}>
                        {deadlineLabel(task.due)}
                      </span>
                    </li>
                  );
                })}
                {agenda.map((event) => (
                  <li key={event.id} className="flex items-center gap-2 px-4 py-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary-500 flex-shrink-0"></span>
                    <p className="text-xs font-medium text-foreground-900 truncate flex-1">{event.title}</p>
                    <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                      {deadlineLabel(event.date)} · {event.time}
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to="/mon-compte/apps/zifek-tasks"
                className="block px-4 py-2.5 text-[11px] font-semibold text-primary-700 border-t border-background-200/50 no-underline hover:bg-background-100/60"
              >
                {t('space.openTasks')} <i className="ri-arrow-right-line"></i>
              </Link>
            </>
          )}
        </section>

        {/* Quiz du jour */}
        <section className="bg-background-50 border border-background-200/50 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-background-200/50">
            <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
              <i className="ri-stack-line"></i>
            </span>
            <h2 className="text-xs font-bold font-heading text-foreground-950 flex-1 truncate">{t('space.quizToday')}</h2>
          </div>
          {!quizDeck ? (
            <div className="px-4 py-8 text-center">
              <p className="text-xs text-foreground-400">{t('space.noQuiz')}</p>
              <Link to="/mon-compte/apps/zifek-flashcards" className="mt-3 inline-block text-[11px] font-semibold text-primary-700 no-underline">
                {t('space.openCards')} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          ) : (
            <>
              <div className="px-4 py-4">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-foreground-400">
                  {dueDecks.length > 0 ? t('cards.due') : 'Prochaine leçon'}
                </p>
                <p className="text-sm font-bold font-heading text-foreground-950 mt-0.5 truncate">{quizDeck.name}</p>
                <p className="text-[11px] text-foreground-500 mt-0.5 truncate">
                  {quizDeck.subject} · {quizDeck.cards.length} {t('cards.cards').toLowerCase()}
                </p>
                <Link
                  to="/mon-compte/apps/zifek-flashcards"
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap no-underline hover:bg-foreground-800"
                >
                  <i className="ri-play-fill"></i>
                  {t('cards.start')}
                </Link>
              </div>
              <Link
                to="/mon-compte/apps/zifek-flashcards"
                className="block px-4 py-2.5 text-[11px] font-semibold text-primary-700 border-t border-background-200/50 no-underline hover:bg-background-100/60"
              >
                {t('space.openCards')} <i className="ri-arrow-right-line"></i>
              </Link>
            </>
          )}
        </section>
      </div>
    </div>
  );
}